import { UT_TYPES } from '../constants';
import { AnalysisRow, UtKey } from '../types';
import { fmt } from './excelUtils';

/**
 * 기계 UT 항목별 분석결과(⑥) 텍스트 생성 — TB_EELMB_UTM_RPT_OPIN_M.ANLS_RLT_CTN 용.
 *
 * ④ 비교 분석표의 AnalysisRow[]를 받아 UT 항목 하나당 한 덩어리의 텍스트를 만든다.
 * 행마다 이미 계산돼 있는 diffU(사용량 차이)/diffQ(수량 차이)를 묶어서 더하기만 하므로,
 * 전체 합계는 ③ 요약 통계의 해당 항목 "차이"와 항상 일치한다(1라인별 모드 여부와 무관).
 */

/** ANLS_RLT_CTN(VARCHAR2(4000)) 초과 방지 — 영향 큰 순으로 이 건수까지만 나열한다 */
const MAX_LINES = 10;

/** 세부공정 한 줄 아래에 붙일 Machine 하위 줄 수 (MAX_LINES와 같은 역할) */
const MAX_MACHINE_LINES = 5;

/** 세부공정명이 비어 있는 행을 묶는 이름 — 빈 문자열이 그대로 제목이 되는 걸 막는다 */
const UNNAMED = '(세부공정 미지정)';

/** Machine명이 비어 있는 행을 묶는 이름 — 하위 줄이 번호만 남는 걸 막는다 */
const UNNAMED_MACHINE = '(Machine 미지정)';

/**
 * 집계 단위 키.
 *
 * 세부공정명(DTAL_PROC_NM)만 쓰면 공정이 달라도 이름이 같은 세부공정이 한 줄로 합쳐진다.
 * 공정까지 구분하려면 `${row.process} - ${sub}` 형태로 바꾸면 된다 — 이 함수만 고치면 된다.
 */
const groupKeyOf = (row: AnalysisRow): string => row.subProcess?.trim() || UNNAMED;

/** 부호를 항상 붙인다. fmt()는 0을 '-'로 내보내므로 0은 따로 처리한다 */
const signedFmt = (v: number, d = 1): string => (v === 0 ? '0' : `${v > 0 ? '+' : ''}${fmt(v, d)}`);

/** UT 항목명에서 단위 표기를 뗀다 — 'CDA [Nl/min]' → 'CDA' */
const utNameOf = (utKey: UtKey): string =>
  (UT_TYPES.find((u) => u.key === utKey)?.label ?? utKey).replace(/\s*\[.*\]\s*$/, '');

/**
 * UT 항목 하나의 분석결과 텍스트.
 *
 * 세부공정 한 줄 아래에 그 안에서 영향이 큰 Machine을 최대 MAX_MACHINE_LINES개까지 들여써
 * 붙인다. 어느 설비가 변동을 만들었는지 텍스트만 보고 짚을 수 있게 하기 위함이다.
 *
 * 예)
 *   CDA (+14,740.6)
 *   1) 코팅 (+48,000.0), Qty (0)
 *      1-1) COATER#1 (+30,000.0), Qty (0)
 *      1-2) COATER#2 (+18,000.0), Qty (0)
 *   2) 스태킹 (-33,239.3), Qty (-2)
 *      2-1) STACKER#3 (-33,239.3), Qty (-2)
 *   외 3건 (+0.0)
 */
export const buildMechUtDiffText = (utKey: UtKey, rows: AnalysisRow[]): string => {
  const utName = utNameOf(utKey);

  interface Acc {
    usage: number;
    qty: number;
    /** 같은 세부공정 안의 Machine별 하위 집계 — 합계(usage/qty)와 동시에 누적한다 */
    byMachine: Map<string, { usage: number; qty: number }>;
  }

  const byGroup = new Map<string, Acc>();
  for (const row of rows) {
    const cell = row.ut[utKey];
    if (!cell) continue;
    const key = groupKeyOf(row);
    const acc = byGroup.get(key) ?? { usage: 0, qty: 0, byMachine: new Map() };
    acc.usage += cell.diffU;
    acc.qty += cell.diffQ;

    // 세부공정 합계는 위에서 이미 더했다. 아래는 표시용 하위 집계라 총합에 영향을 주지 않는다.
    const machine = row.machine?.trim() || UNNAMED_MACHINE;
    const mAcc = acc.byMachine.get(machine) ?? { usage: 0, qty: 0 };
    mAcc.usage += cell.diffU;
    mAcc.qty += cell.diffQ;
    acc.byMachine.set(machine, mAcc);

    byGroup.set(key, acc);
  }

  // 사용량 변동이 없어도 수량이 바뀐 건 남긴다 — 설비 대수만 바뀐 경우를 놓치지 않기 위함
  const entries = Array.from(byGroup.entries()).filter(([, v]) => v.usage !== 0 || v.qty !== 0);
  if (entries.length === 0) return `${utName} (변동 없음)`;

  const totalUsage = entries.reduce((s, [, v]) => s + v.usage, 0);
  entries.sort((a, b) => Math.abs(b[1].usage) - Math.abs(a[1].usage));

  const shown = entries.slice(0, MAX_LINES);
  const rest = entries.slice(MAX_LINES);

  const lines: string[] = [];
  shown.forEach(([name, v], i) => {
    lines.push(`${i + 1}) ${name} (${signedFmt(v.usage)}), Qty (${signedFmt(v.qty, 0)})`);
    // 세부공정 줄 바로 아래에 영향 큰 Machine을 붙인다.
    // 세부공정과 달리 사용량이 그대로인 Machine은 뺀다 — 대수만 바뀐 건 상위 줄의 Qty로 이미 보인다.
    Array.from(v.byMachine.entries())
      .filter(([, mv]) => mv.usage !== 0)
      .sort((a, b) => Math.abs(b[1].usage) - Math.abs(a[1].usage))
      .slice(0, MAX_MACHINE_LINES)
      .forEach(([machine, mv], j) => {
        lines.push(
          `   ${i + 1}-${j + 1}) ${machine} (${signedFmt(mv.usage)}), Qty (${signedFmt(mv.qty, 0)})`
        );
      });
  });
  if (rest.length > 0) {
    const restUsage = rest.reduce((s, [, v]) => s + v.usage, 0);
    lines.push(`외 ${rest.length}건 (${signedFmt(restUsage)})`);
  }

  return `${utName} (${signedFmt(totalUsage)})\n${lines.join('\n')}`;
};

/** UT 9종 전체 — UtKey → 분석결과 텍스트 */
export const buildAllMechUtDiffTexts = (rows: AnalysisRow[]): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const ut of UT_TYPES) result[ut.key] = buildMechUtDiffText(ut.key, rows);
  return result;
};
